// AuthContext — Phase 1 Plan 05.
//
// Provides:
// - On mount: reads initData.raw via @telegram-apps/sdk-react, exchanges for
//   JWT + refresh via POST /api/v1/auth/exchange-init-data.
// - Tokens persisted to localStorage (Phase 1 acceptable per threat model
//   T-01-05-07; Phase 5 refactors to httpOnly cookies при admin endpoints).
// - Provides ApiClient configured with token + refresh callback.
// - useAuth() hook exposes { isAuthenticated, isLoading, error, user, jwt,
//   initDataRaw, apiClient, exchange, refresh, logout }.

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { type AuthResponse, exchangeInitData, refreshAccess } from "../api/auth";
import { ApiClient } from "../api/client";

const STORAGE_JWT = "sd:jwt";
const STORAGE_REFRESH = "sd:refresh";

export interface AuthUser {
  telegramId: number;
  userId?: number;
  companyId?: number | null;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  jwt: string | null;
  user: AuthUser | null;
  initDataRaw: string | null;
  apiClient: ApiClient;
  exchange: (initDataRaw: string) => Promise<void>;
  refresh: () => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export interface AuthProviderProps {
  baseUrl: string;
  initDataRaw: string | null;
  children: ReactNode;
}

export function AuthProvider({
  baseUrl,
  initDataRaw,
  children,
}: AuthProviderProps): React.JSX.Element {
  const [jwt, setJwt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_JWT);
    } catch {
      return null;
    }
  });
  const [refreshToken, setRefreshToken] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_REFRESH);
    } catch {
      return null;
    }
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const persist = useCallback((tokens: AuthResponse | null) => {
    if (tokens === null) {
      setJwt(null);
      setRefreshToken(null);
      try {
        localStorage.removeItem(STORAGE_JWT);
        localStorage.removeItem(STORAGE_REFRESH);
      } catch {
        // localStorage unavailable in some test envs — accept silently.
      }
      return;
    }
    setJwt(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
    try {
      localStorage.setItem(STORAGE_JWT, tokens.access_token);
      localStorage.setItem(STORAGE_REFRESH, tokens.refresh_token);
    } catch {
      // ignored — see above
    }
  }, []);

  const exchange = useCallback(
    async (rawInitData: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const tokens = await exchangeInitData(baseUrl, rawInitData);
        persist(tokens);

        // Parse telegram_id from initData query string for user state (defensive).
        let telegramId = 0;
        try {
          const params = new URLSearchParams(rawInitData);
          const userJson = params.get("user");
          if (userJson) {
            const parsed = JSON.parse(userJson) as { id?: number };
            telegramId = parsed.id ?? 0;
          }
        } catch {
          // best-effort parse — telegramId remains 0
        }
        setUser({ telegramId, companyId: null });
      } catch (e) {
        setError((e as Error).message);
        persist(null);
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, persist],
  );

  const doRefresh = useCallback(async (): Promise<string | null> => {
    if (!refreshToken) {
      return null;
    }
    try {
      const tokens = await refreshAccess(baseUrl, refreshToken);
      persist(tokens);
      return tokens.access_token;
    } catch {
      persist(null);
      return null;
    }
  }, [baseUrl, persist, refreshToken]);

  const logout = useCallback(() => {
    persist(null);
    setUser(null);
  }, [persist]);

  // Auto-exchange on mount всегда, когда есть initData raw. Telegram выдаёт
  // свежий initData при каждом открытии Mini App, а сохранённый JWT может
  // устареть не только по exp, но и по claims: токен, выданный ДО регистрации
  // компании, несёт cid=null навсегда — entry-redirect по cid не срабатывал
  // и walking skeleton зацикливался (bug 2026-06-12).
  useEffect(() => {
    if (initDataRaw) {
      void exchange(initDataRaw);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDataRaw]);

  const apiClient = useMemo(
    () =>
      new ApiClient({
        baseUrl,
        getToken: () => jwt,
        refresh: () => doRefresh(),
      }),
    [baseUrl, jwt, doRefresh],
  );

  const value: AuthState = useMemo(
    () => ({
      isAuthenticated: Boolean(jwt),
      isLoading,
      error,
      jwt,
      user,
      initDataRaw,
      apiClient,
      exchange,
      refresh: doRefresh,
      logout,
    }),
    [jwt, isLoading, error, user, initDataRaw, apiClient, exchange, doRefresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used inside AuthProvider");
  }
  return ctx;
}
